import type { Activity } from '@/lib/apiClient';

/**
 * The Schedule tab's list, in the order and shape MS Project shows it (#738).
 *
 * An MS Project file is an outline: summary tasks ("Phase 1", an area, a building) are the
 * headers, and the tasks under them are indented. The import has always stored both
 * (`is_summary`, `outline_level`, `sort_order` = file order), but the tab used to drop the
 * summary rows and re-sort what was left by start date, so the headers never appeared and the
 * phases ran together. This puts the file's own order back.
 */
export interface OutlineRow {
  activity: Activity;
  /** True for a summary task: drawn as a header row over the tasks beneath it. */
  isHeader: boolean;
  /** How far to indent, 0 for the top level of the outline. */
  depth: number;
}

/**
 * MS Project's project summary row is UID 0: the whole job under its own name, which the page
 * already says. Only a summary row can be it, so a detail task is never hidden by this, and
 * it goes by UID alone so that no real summary task can be taken for it by its outline level.
 */
function isProjectSummaryRow(a: Activity): boolean {
  return a.isSummary && a.uid === 0;
}

/**
 * The activities as outline rows: file order, summary tasks as headers, depth from the outline
 * level (level 1 is depth 0). The API returns file order already; sorting here keeps the list
 * right for any caller that did not, and `uid` breaks a tie so the order is always the same.
 *
 * A row with no outline level is shown flat rather than guessed at.
 */
export function outlineRows(activities: Activity[]): OutlineRow[] {
  return [...activities]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.uid - b.uid)
    .filter((a) => !isProjectSummaryRow(a))
    .map((activity) => ({
      activity,
      isHeader: activity.isSummary,
      depth: activity.outlineLevel != null ? Math.max(0, activity.outlineLevel - 1) : 0,
    }));
}
